import { Voucher } from '../types';
import { PdfTablePropsColumn, PdfTablePropsRow } from '../../../components/Pdfs/PdfComponents/PdfTable';
import { BillingContactPDFType } from '../../../components/Pdfs/PdfComponents/PdfBillingContactDetails';
import { taytanFormatNumberToString } from '../../../utils/mathUtils';
import { getPdfSupplierInfo } from '../../supplierAccounts/helpers';
import { getPdfAgentInfo } from '../../travelAgencyAccounts/helpers';
import { PdfInfoBuilderContext, BuiltPdfInfo } from './types';
import { getCurrencyDetails, getClientCurrencies, buildRateLine } from './helpers';

/**
 * Build pdfInfo for Tax Invoice
 *
 * Fixes from audit:
 * - Row label "Amount" (was "Mark-Up")
 * - rateLine text (was hardcoded null)
 * - equivalentAmountText computed when currencies differ
 * - VAT condition aligned with inline (vatAmount !== 0)
 */
export function buildTaxInvoicePdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext,
    baseInfo: BuiltPdfInfo
): BuiltPdfInfo {
    const info = voucher.info as any;
    const { localCurrency, primaryCurrency } = getClientCurrencies(context.client, context.currencies);
    const { code: currencyCode, currency: docCurrency } = getCurrencyDetails(
        info?.currency,
        context.currencies
    );

    // Passenger: prefer joined documentpassenger table, fall back to embedded info.passenger
    const rawPassenger = voucher.passenger;
    const passenger = Array.isArray(rawPassenger) ? rawPassenger[0] : rawPassenger || info?.passenger;

    // ── Secondary currency column ─────────────────────────────────────────────
    // Normally the client's LOCAL currency (e.g. ILS). But when the document is
    // itself in the local currency, a second ILS column is redundant — show the
    // PRIMARY currency (e.g. USD) instead, converted at the document-date rate.
    const isLocalDoc = !!localCurrency && !!docCurrency && docCurrency._id === localCurrency._id;
    const secondaryCurrency = isLocalDoc ? primaryCurrency : localCurrency;
    const secondaryLabel = secondaryCurrency?.code || '';

    // Per-document toggle (info.printLocalCurrency). Legacy docs (undefined) keep
    // the column. Never render a second column that duplicates the primary one.
    const showSecondary =
        info?.printLocalCurrency !== false &&
        !!secondaryCurrency &&
        secondaryLabel !== currencyCode;

    // ── Columns ──────────────────────────────────────────────────────────────
    const columns: PdfTablePropsColumn[] = [
        { title: 'Details', fieldName: 'details', style: { width: '60%' } },
        { title: currencyCode, fieldName: 'amount', style: { width: '20%' } }
    ];

    const localLabel = secondaryLabel;
    if (showSecondary) {
        columns.push({
            title: secondaryLabel,
            fieldName: secondaryLabel,
            style: { width: '20%' }
        });
    }

    // ── Amounts ──────────────────────────────────────────────────────────────
    const amount = Number(info?.amount) || 0;
    const vatAmount = Number(info?.vatOnCommission) || Number(info?.vat) || 0;
    // Mark-Up: prefer stored markup, otherwise derive from total - VAT
    const baseAmount = Number(info?.markup) || (amount - vatAmount);

    // `info.rate` is the doc→local rate for a foreign doc, or the local↔primary
    // (e.g. USD→ILS) rate for a local doc (see RateControlBox). Fall back to a
    // live lookup in the matching direction.
    const fallbackRate = isLocalDoc
        ? context.getConvertRate(primaryCurrency, localCurrency)
        : context.getConvertRate(info?.currency, localCurrency);
    const rateToLocal = Number(info?.rate) || fallbackRate?.exchangeRateSell || 1;

    // For a foreign doc the second column is the LOCAL currency, and the doc
    // stores `amountLocal` = the exact ILS total (the same value the reports and
    // Export-to-GL use). Derive the column from it so the PDF, reports, and the
    // saved value never drift by a rounding cent — and so a stale/market rate
    // can never leak into the display. Falls back to rate when amountLocal is
    // missing (legacy rows) or for a local-currency doc (secondary is USD).
    const storedLocal = Number(info?.amountLocal) || 0;
    const effectiveLocalRate = !isLocalDoc && storedLocal > 0 && amount > 0 ? storedLocal / amount : rateToLocal;

    const round2 = (n: number): number => Math.round(n * 100) / 100;
    // Foreign doc: secondary (local) = amount * effectiveLocalRate. Local doc:
    // secondary (primary/USD) = amount / rate (rate is USD→local for the date).
    const toSecondary = (n: number): number =>
        isLocalDoc ? (rateToLocal ? n / rateToLocal : n) : n * effectiveLocalRate;
    // Total ties to the exact stored local amount (foreign doc); Mark-Up is
    // rounded and VAT is the RESIDUAL so the breakdown always sums to the total
    // (e.g. 100.00 + 18.00 = 118.00, never 100.00 + 17.99 = 117.99).
    const totalLocal = !isLocalDoc && storedLocal > 0 ? storedLocal : round2(toSecondary(amount));
    const baseAmountLocal = round2(toSecondary(baseAmount));
    const vatLocal = vatAmount !== 0 ? round2(totalLocal - baseAmountLocal) : 0;

    // ── Rows ─────────────────────────────────────────────────────────────────
    const rows: PdfTablePropsRow[] = [
        {
            details: 'Mark-Up',
            amount: taytanFormatNumberToString(baseAmount),
            [localLabel]: taytanFormatNumberToString(baseAmountLocal)
        }
    ];

    if (vatAmount !== 0) {
        rows.push({
            details: 'VAT',
            amount: taytanFormatNumberToString(vatAmount),
            [localLabel]: taytanFormatNumberToString(vatLocal)
        });
    }

    const summaryLine: PdfTablePropsRow = {
        details: 'Total',
        amount: taytanFormatNumberToString(amount),
        [localLabel]: taytanFormatNumberToString(totalLocal)
    };

    // ── Contact info ─────────────────────────────────────────────────────────
    let supplierInfo: BillingContactPDFType | null = null;
    let agentInfo: BillingContactPDFType | null = null;

    if (voucher.sourceType === 'supplier' && context.supplierAccount) {
        supplierInfo = getPdfSupplierInfo(context.supplierAccount);
    }
    if (voucher.sourceType === 'agent' && context.travelAgencyAccount) {
        agentInfo = getPdfAgentInfo(context.travelAgencyAccount);
    }

    // ── Rate line (simple text instead of table) ─────────────────────────────
    const rateLine = buildRateLine(currencyCode, rateToLocal, localCurrency?.code || '');

    // No equivalent text needed — table already shows both currency columns

    return {
        ...baseInfo,
        columns,
        rows,
        summaryLine,
        topLeftDetailsText: info?.beneficiary?.[0]?.name || info?.travelAgentName || null,
        bottomDetails: info?.for || '',
        supplierInfo,
        agentInfo,
        passenger,
        rateLine,
        rateTableColumns: [],
        rateTableRows: [],
        equivalentAmountText: null,
        // Tax Authority allocation number (מספר הקצאה), once obtained.
        allocationNumber: info?.allocationNumber || null
    };
}
